import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  EvaluateSupportConversationRequest,
  UpdateAllCommentStatusRequest,
  UpdateAllPostStatusRequest,
  UpdateCommentStatusRequest,
  UpdatePostStatusRequest,
  ViewApprovalCountRequest,
  ViewApprovalCountResponse,
  ViewApprovalMembersRequest,
  ViewApprovalMembersResponse,
  ViewApprovalsForUserRequest,
  ViewApprovalsForUserResponse,
  ViewApprovalsRequest,
  ViewApprovalsResponse
} from './approval.types';

import {
  EVALUATING_SUPPORT_CONVERSATION,
  RETRIEVING_APPROVALS,
  RETRIEVING_MEMBERS,
  UPDATING_ALL_COMMENTS,
  UPDATING_ALL_POSTS,
  UPDATING_COMMENT_STATUS,
  UPDATING_POST_STATUS
} from './approval.constants';

import {
  requestEvaluateSupportConversation,
  requestUpdateAllCommentStatus,
  requestUpdateAllPostStatus,
  requestUpdateCommentStatus,
  requestUpdatePostStatus,
  requestViewApprovalCount,
  requestViewApprovalMembers,
  requestViewApprovals,
  requestViewApprovalsForUser
} from './approval.actions';

import {
  getEvaluateSupportConversationRequest,
  getUpdateAllCommentStatusRequest,
  getUpdateAllPostStatusRequest,
  getUpdateCommentStatusRequest,
  getUpdatePostStatusRequest,
  getViewApprovalCountRequest,
  getViewApprovalMembersRequest,
  getViewApprovalsForUserRequest,
  getViewApprovalsRequest
} from './approval.endpoints';

import { setApprovalCount, setApprovalMembers, setPosts } from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performViewApprovalCountRequest({ payload }: SagaPayload<ViewApprovalCountRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    const [endpoint, options] = getViewApprovalCountRequest(requestData);

    const { data }: AxiosResponse<ViewApprovalCountResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setApprovalCount(data.pendingApprovalCount));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewApprovalCountRequest() {
  yield takeLatest(requestViewApprovalCount.type, performViewApprovalCountRequest);
}

export function* performViewApprovalMembersRequest({ payload }: SagaPayload<ViewApprovalMembersRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBERS));

    const [endpoint, options] = getViewApprovalMembersRequest(requestData);

    const { data }: AxiosResponse<ViewApprovalMembersResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setApprovalMembers(data.memberApprovalDetails));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBERS));
  }
}

export function* watchForViewApprovalMembersRequest() {
  yield takeLatest(requestViewApprovalMembers.type, performViewApprovalMembersRequest);
}

export function* performViewApprovalsRequest({ payload }: SagaPayload<ViewApprovalsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_APPROVALS));

    const [endpoint, options] = getViewApprovalsRequest(requestData);

    const { data }: AxiosResponse<ViewApprovalsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setPosts(data.posts));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_APPROVALS));
  }
}

export function* watchForViewApprovalsRequest() {
  yield takeLatest(requestViewApprovals.type, performViewApprovalsRequest);
}

export function* performUpdatePostStatusRequest({ payload }: SagaPayload<UpdatePostStatusRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_POST_STATUS));

    const [endpoint, options] = getUpdatePostStatusRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_POST_STATUS));
  }
}

export function* watchForUpdatePostStatusRequest() {
  yield takeLatest(requestUpdatePostStatus.type, performUpdatePostStatusRequest);
}

export function* performUpdateCommentStatusRequest({ payload }: SagaPayload<UpdateCommentStatusRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_COMMENT_STATUS));

    const [endpoint, options] = getUpdateCommentStatusRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_COMMENT_STATUS));
  }
}

export function* watchForUpdateCommentStatusRequest() {
  yield takeLatest(requestUpdateCommentStatus.type, performUpdateCommentStatusRequest);
}

export function* performUpdateAllPostStatusRequest({ payload }: SagaPayload<UpdateAllPostStatusRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_ALL_POSTS));

    const [endpoint, options] = getUpdateAllPostStatusRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_ALL_POSTS));
  }
}

export function* watchForUpdateAllPostStatusRequest() {
  yield takeLatest(requestUpdateAllPostStatus.type, performUpdateAllPostStatusRequest);
}

export function* performUpdateAllCommentStatusRequest({ payload }: SagaPayload<UpdateAllCommentStatusRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_ALL_COMMENTS));

    const [endpoint, options] = getUpdateAllCommentStatusRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_ALL_COMMENTS));
  }
}

export function* watchForUpdateAllCommentStatusRequest() {
  yield takeLatest(requestUpdateAllCommentStatus.type, performUpdateAllCommentStatusRequest);
}

export function* performViewApprovalsForUserRequest({ payload }: SagaPayload<ViewApprovalsForUserRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_APPROVALS));

    const [endpoint, options] = getViewApprovalsForUserRequest(requestData);

    const { data }: AxiosResponse<ViewApprovalsForUserResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setPosts(data.posts));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_APPROVALS));
  }
}

export function* watchForViewApprovalsForUserRequest() {
  yield takeLatest(requestViewApprovalsForUser.type, performViewApprovalsForUserRequest);
}

export function* performEvaluateSupportConversationRequest({
  payload
}: SagaPayload<EvaluateSupportConversationRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(EVALUATING_SUPPORT_CONVERSATION));

    const [endpoint, options] = getEvaluateSupportConversationRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(EVALUATING_SUPPORT_CONVERSATION));
  }
}

export function* watchForEvaluateSupportConversationRequest() {
  yield takeLatest(requestEvaluateSupportConversation.type, performEvaluateSupportConversationRequest);
}

export default function* approvalSaga() {
  yield all([
    watchForViewApprovalCountRequest(),
    watchForViewApprovalMembersRequest(),
    watchForViewApprovalsRequest(),
    watchForUpdatePostStatusRequest(),
    watchForUpdateCommentStatusRequest(),
    watchForUpdateAllPostStatusRequest(),
    watchForUpdateAllCommentStatusRequest(),
    watchForViewApprovalsForUserRequest(),
    watchForEvaluateSupportConversationRequest()
  ]);
}
